import {
  Box,
  Button,
  Card,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import { object, string, ref } from "yup";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { InputAdornment, IconButton } from "@mui/material";
import WDButton from "../../../MaterialComponents/WDButton";
import { useEffect, useState } from "react";
import AppService from "../../../Configurations/Appservice";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  dismissLoader,
  showLoader,
  showSuccessMessage,
  updateScreenTitle,
} from "../../../LayoutContainers/AppLayout/redux/AppSlice";

const getValidationSchema = (paymentMethodId, accountNumberChanged) => {
  let schema = object({
    paymentMethodId: string().required("Payment Method is Mandatory"),
  });

  if (paymentMethodId === "BANK_TRANSFER") {
    let bankTransferSchema = {
      bankName: string().required("Bank Name is required for Bank Transfer"),
      accountNumber: string().required("Account Number is required"),
    };
    
    // Only require confirmation if account number was changed
    if (accountNumberChanged) {
      bankTransferSchema.confirmAccountNumber = string()
        .required("Please confirm your account number")
        .oneOf([ref('accountNumber')], 'Account numbers must match');
    }
    
    schema = schema.shape(bankTransferSchema);
  } else if (paymentMethodId === "CHEQUE") {
    schema = schema.shape({
      bankName: string().required("Bank Name is required for Cheque"),
    });
  } else if (paymentMethodId === "CASH_DEPOSIT") {
    schema = schema.shape({
      bankName: string().required("Bank Name is required for Cash Deposit"),
    });
  }

  return schema;
};

const CustomerPaymentMethodEdit = (props) => {
  const { id } = useParams();
  const [paymentMethods, setPaymentMethods] = useState();
  const [initialValues, setInitialValues] = useState(null);
  const [originalAccountNumber, setOriginalAccountNumber] = useState("");
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  console.log("Edit page loaded with ID:", id);

  useEffect(() => {
    dispatch(updateScreenTitle("Edit Customer Payment Method"));
    return () => dispatch(updateScreenTitle(""));
  }, []);

  useEffect(() => {
    // Load payment method types
    AppService.get("v1/paymentMethod/keyValues")
      .then((response) => {
        console.log("Payment methods loaded:", response);
        setPaymentMethods(response);
      })
      .catch((err) => {
        console.error("Failed to load payment methods:", err);
      });
  }, []);

  useEffect(() => {
    if (!id) {
      console.error("No ID provided to edit page");
      return;
    }

    dispatch(showLoader());
    console.log(`Fetching payment method with ID: ${id}`);

    AppService.get(`v1/customerPaymentMethod/${id}`)
      .then((response) => {
        console.log("Payment method fetched successfully:", response);
        const accNum = response?.accountNumber || "";
        setOriginalAccountNumber(accNum);
        setInitialValues({
          paymentMethodId: response?.paymentMethodType || "",
          comment: response?.comment || "",
          bankName: response?.bankName || "",
          accountNumber: accNum,
          confirmAccountNumber: "",
          iban: response?.iban || "",
          branch: response?.branch || "",
        });
      })
      .catch((error) => {
        console.error("Failed to fetch payment method:", error);
        dispatch(
          showSuccessMessage("Payment method not found or failed to load")
        );
        setTimeout(() => navigate("/wallet/payment_methods"), 2000);
      })
      .finally(() => {
        dispatch(dismissLoader());
        setLoading(false);
      });
  }, [id]);

  const _onSubmit = (values, { resetForm }) => {
    dispatch(showLoader());
    console.log("Submitting edit form with values:", values);

    AppService.put(`v1/customerPaymentMethod/${id}`, values)
      .then((response) => {
        console.log("Payment method updated successfully:", response);
        dispatch(showSuccessMessage("Payment method updated successfully"));
        setTimeout(() => {
          navigate("/wallet/payment_methods");
        }, 1500);
      })
      .catch((error) => {
        console.error("Failed to update payment method:", error);
        dispatch(
          showSuccessMessage("Failed to update payment method")
        );
      })
      .finally(() => {
        dispatch(dismissLoader());
      });
  };

  if (loading || !initialValues) {
    return (
      <Box>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Card sx={{ padding: 3 }}>
        <Box pb={3}>
          <Typography fontSize={16}>Edit Payment Method</Typography>
        </Box>
        <Formik
          initialValues={initialValues}
          onSubmit={_onSubmit}
          validationSchema={(values) => {
            if (!values) return object({});
            const accountNumberChanged = values.accountNumber !== originalAccountNumber;
            return getValidationSchema(values.paymentMethodId || '', accountNumberChanged);
          }}
          enableReinitialize
        >
          {({ errors, isValid, touched, dirty, setFieldValue, values }) => (
            <Form>
              <Grid container spacing={2}>
                {/* Payment Method Type */}
                <Grid item xs={6}>
                  <Box>
                    <FormControl fullWidth>
                      <InputLabel id="method-label">Payment Method Type</InputLabel>
                      <Field
                        name="paymentMethodId"
                        label="Payment Method Type"
                        labelId="method-label"
                        size="small"
                        fullWidth
                        as={Select}
                        disabled
                        onChange={(e) => {
                          setFieldValue("paymentMethodId", e.target.value);
                          if (e.target.value !== "BANK_TRANSFER") {
                            setFieldValue("bankName", "");
                            setFieldValue("accountNumber", "");
                            setFieldValue("iban", "");
                          }
                        }}
                        error={Boolean(errors.paymentMethodId) && Boolean(touched.paymentMethodId)}
                      >
                        {paymentMethods ? (
                          paymentMethods.map((method) => (
                            <MenuItem key={method.id} value={method.id}>
                              {method.name}
                            </MenuItem>
                          ))
                        ) : (
                          <Typography fontSize={14}>Loading...</Typography>
                        )}
                      </Field>
                    </FormControl>
                  </Box>
                </Grid>

                {/* Bank Transfer Fields */}
                {values.paymentMethodId === "BANK_TRANSFER" && (
                  <>
                    <Grid item xs={6}>
                      <Field
                        name="bankName"
                        label="Bank Name"
                        fullWidth
                        as={TextField}
                        size="small"
                        placeholder="e.g., HDFC Bank"
                        error={Boolean(errors.bankName) && Boolean(touched.bankName)}
                        helperText={Boolean(touched.bankName) && errors.bankName}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        name="accountNumber">
                        {({ field }) => (
                          <TextField
                            {...field}
                            label="Account Number"
                            fullWidth
                            size="small"
                            type={showAccountNumber ? "text" : "password"}
                            placeholder="e.g., 1234567890"
                            error={Boolean(errors.accountNumber) && Boolean(touched.accountNumber)}
                            helperText={Boolean(touched.accountNumber) && errors.accountNumber}
                            onChange={(e) => {
                              field.onChange(e);
                              // Clear confirmation when account number changes
                              if (e.target.value !== originalAccountNumber) {
                                setFieldValue('confirmAccountNumber', '');
                              }
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setShowAccountNumber(!showAccountNumber)}
                                    edge="end"
                                    size="small"
                                  >
                                    {showAccountNumber ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                    {/* Show confirm field only when account number is changed */}
                    {values.accountNumber !== originalAccountNumber && (
                      <Grid item xs={6}>
                        <Field
                          name="confirmAccountNumber">
                          {({ field }) => (
                            <TextField
                              {...field}
                              label="Confirm Account Number"
                              fullWidth
                              size="small"
                              type="password"
                              placeholder="Re-enter account number"
                              error={(values.confirmAccountNumber && values.accountNumber !== values.confirmAccountNumber) || (Boolean(errors.confirmAccountNumber) && Boolean(touched.confirmAccountNumber))}
                              helperText={
                                values.confirmAccountNumber && values.accountNumber !== values.confirmAccountNumber
                                  ? 'Account numbers do not match'
                                  : (Boolean(touched.confirmAccountNumber) && errors.confirmAccountNumber)
                              }
                            />
                          )}
                        </Field>
                      </Grid>
                    )}
                    <Grid item xs={6}>
                      <Field
                        name="iban"
                        label="IBAN (Optional)"
                        fullWidth
                        as={TextField}
                        size="small"
                        placeholder="e.g., AE070331234567890123456"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        name="branch"
                        label="Branch"
                        fullWidth
                        as={TextField}
                        size="small"
                        placeholder="e.g., Downtown Dubai"
                      />
                    </Grid>
                  </>
                )}

                {/* Cheque Fields */}
                {values.paymentMethodId === "CHEQUE" && (
                  <Grid item xs={6}>
                    <Field
                      name="bankName"
                      label="Bank Name"
                      fullWidth
                      as={TextField}
                      size="small"
                      placeholder="e.g., Emirates Bank"
                      error={Boolean(errors.bankName) && Boolean(touched.bankName)}
                      helperText={Boolean(touched.bankName) && errors.bankName}
                    />
                  </Grid>
                )}

                {/* Cash Deposit Fields */}
                {values.paymentMethodId === "CASH_DEPOSIT" && (
                  <Grid item xs={6}>
                    <Field
                      name="bankName"
                      label="Bank Name"
                      fullWidth
                      as={TextField}
                      size="small"
                      placeholder="e.g., ADIB"
                      error={Boolean(errors.bankName) && Boolean(touched.bankName)}
                      helperText={Boolean(touched.bankName) && errors.bankName}
                    />
                  </Grid>
                )}

                {/* Description/Notes - Always show */}
                <Grid item xs={12}>
                  <Field
                    name="comment"
                    label="Additional Notes/Description"
                    fullWidth
                    as={TextField}
                    size="small"
                    multiline
                    minRows={3}
                    placeholder="e.g., Primary account, Account holder: John Doe"
                  />
                </Grid>
              </Grid>
              <Box display="flex" justifyContent="flex-end" gap={1} pt={3}>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/wallet/payment_methods")}
                  sx={{
                    color: "#1f2a44",          // or theme.palette.primary.main
                    borderColor: "#1f2a44",
                    "&:hover": {
                      borderColor: "#1f2a44",
                      backgroundColor: "rgba(31,42,68,0.04)",
                    },
                  }}
                >
                  Cancel
                </Button>
                <WDButton 
                  variant="gradient" 
                  disabled={!dirty || !isValid || (values.paymentMethodId === "BANK_TRANSFER" && values.accountNumber !== originalAccountNumber && values.accountNumber !== values.confirmAccountNumber)} 
                  type="submit"
                >
                  Update Payment Method
                </WDButton>
              </Box>
            </Form>
          )}
        </Formik>
      </Card>
    </Box>
  );
};

export default CustomerPaymentMethodEdit;
